'use client';
import { useEffect, useState } from 'react';
import type { EnvironmentOperationFollowState } from 'elowen-plugin-ui-kit';
import { elowenClient } from './elowenClient';
import { subscribePluginEvents } from './pluginEvents';
import { isEnvironmentOperation } from './environmentActions';

/** The kind the sandbox plugin publishes one lifecycle operation's live state under. */
export const ENVIRONMENT_OPERATION_EVENT = 'environment-operation';

const EMPTY: EnvironmentOperationFollowState = { operation: null, logTail: [], loading: true, loadError: null };

/** Follow one durable environment operation.
 *
 *  One read to seed, then the daemon pushes. The operation row carries its own declared step list and
 *  percent, and the plugin republishes the row on every step, so there is nothing left for a timer to
 *  discover — a dialog opened halfway through a fifteen-minute image build shows the same thing a dialog
 *  opened at the start does, and a dialog left open costs no requests at all.
 *
 *  `operationId` null simply parks the hook, which is what lets a call site mount it unconditionally and
 *  hand it an id when the user starts something. */
export function useEnvironmentOperation(operationId: string | null, projectId?: number): EnvironmentOperationFollowState {
  const [view, setView] = useState<EnvironmentOperationFollowState>(EMPTY);
  useEffect(() => {
    if (!operationId) { setView(EMPTY); return; }
    let live = true;
    setView(EMPTY);
    const controller = new AbortController();
    void (async () => {
      try {
        const body = await elowenClient.environmentOperation(operationId, projectId, controller.signal);
        if (!live) return;
        // A frame that already arrived on the bus is NEWER than this read: the seed must not overwrite it.
        setView((current) => current.operation ? { ...current, loading: false }
          : { operation: body, logTail: body?.logTail ?? [], loading: false, loadError: null });
      } catch (error) {
        if (live) setView((current) => ({ ...current, loading: false, loadError: error instanceof Error ? error.message : String(error) }));
      }
    })();
    const off = subscribePluginEvents((event) => {
      if (!live || event.plugin !== 'sandbox' || event.kind !== ENVIRONMENT_OPERATION_EVENT) return;
      const payload = event.data as { operation?: unknown; logTail?: string[] } | undefined;
      if (!isEnvironmentOperation(payload?.operation) || payload.operation.id !== operationId) return;
      setView({ operation: payload.operation, logTail: payload.logTail ?? [], loading: false, loadError: null });
    });
    return () => { live = false; controller.abort(); off(); };
  }, [operationId, projectId]);
  return view;
}
